/*
* Description: Configure server endpoints dynamically
*
*/

import { BuildModeType, AppConfigurationInfo, OSInfo, AppBuildInformation } from "@preveil-api";
import { BuildMode, RunMode, AuthenticationConstants, account_types, LocalAccountStorage, SupportedPlatform, NODE_ENV, Helpers } from "src/common";
import { ServerConfiguration, EnvConfigKeys } from "./types";

export class AppConfiguration implements AppConfigurationInfo, AppBuildInformation {
  // App Info
  crypto_server: string; // crypto connection
  crypto_websocket_server: string; // Websocket to Crypto (for web connect)
  // NOTE: Current working url to the app (REACT_APP_WEB_PORT for web and REACT_APP_PORT for app build mode)
  local_app_server: string;
  local_web_server: string;
  backend_server: string;
  backend_websocket_server: string;
  filesync_server: string;
  filesync_websocket_server: string;
  keystorage_server: string;
  recaptcha_key: string;
  // Build Info
  build_id?: string;
  head_commit_hash?: string;
  version?: string;
  build_mode?: BuildModeType;
  os_info?: OSInfo;
  browser?: string;

  constructor(data?: ServerConfiguration) {
    this.os_info = this.getOSInfo();
    this.browser = Helpers.getBrowser();
    this.build_mode = `${process.env.REACT_APP_BUILD_MODE as BuildModeType}`;
    this.local_app_server = this._getConfigValue(EnvConfigKeys.REACT_APP_PORT, data);
    this.local_web_server = this._getConfigValue(EnvConfigKeys.REACT_APP_WEB_PORT, data);
    this.crypto_server = `${this._getConfigValue(EnvConfigKeys.REACT_APP_CRYPTO_SERVER, data)}`;
    this.crypto_websocket_server = `${this._getConfigValue(EnvConfigKeys.REACT_APP_CRYPTO_WEBSOCKET_SERVER, data)}`;
    this.backend_server = `${this._getConfigValue(EnvConfigKeys.REACT_APP_BACKEND_SERVER, data)}`;
    this.backend_websocket_server = `${this._getConfigValue(EnvConfigKeys.REACT_APP_BACKEND_WEBSOCKET_SERVER, data)}`;
    this.filesync_server = `${this._getConfigValue(EnvConfigKeys.REACT_APP_FILESYNC_SERVER, data)}`;
    this.filesync_websocket_server = `${this._getConfigValue(EnvConfigKeys.REACT_APP_FILESYNC_WEBSOCKET_SERVER, data)}`;
    this.keystorage_server = `${this._getConfigValue(EnvConfigKeys.REACT_APP_KEYSTORAGE_SERVER, data)}`;
    this.recaptcha_key = `${this._getConfigValue(EnvConfigKeys.REACT_APP_RECAPTCHA_KEY, data)}`;
  }

  get info(): AppConfigurationInfo {
    return {
      crypto_server: this.crypto_server,
      crypto_websocket_server: this.crypto_websocket_server,
      local_app_server: this.local_app_server,
      local_web_server: this.local_web_server,
      backend_server: this.backend_server,
      backend_websocket_server: this.backend_websocket_server,
      filesync_server: this.filesync_server,
      filesync_websocket_server: this.filesync_websocket_server,
      keystorage_server: this.keystorage_server,
      recaptcha_key: this.recaptcha_key
    };
  }

  get build(): AppBuildInformation {
    return {
      build_id: this.build_id,
      head_commit_hash: this.head_commit_hash,
      version: this.version,
      build_mode: this.build_mode,
      os_info: this.os_info,
      browser: this.browser,
    };
  }

  // Description: get the data valued from config or default to react .env variables
  private _getConfigValue(key: keyof ServerConfiguration, data?: ServerConfiguration) {
    return `${!!data ? data[key] : process.env[key]}`;
  }

  buildForWeb(): boolean {
    return this.build_mode === BuildMode.web;
  }

  buildForPreveilExpress(): boolean {
    return this.build_mode === BuildMode.express;
  }

  buildForApp(): boolean {
    return this.build_mode === BuildMode.app;
  }

  getOSInfo(): OSInfo {
    let user_platform: string;
    let default_mua: string;
    let file_manager: string;
    let data_export_example_path: string;

    if (navigator.userAgent.search("Mac") !== -1) {
      user_platform = SupportedPlatform.OSX;
      file_manager = "Finder";
      default_mua = "Apple Mail";
      data_export_example_path = "/Users/(username)/Desktop/New-Export-Folder";
    } else if (navigator.userAgent.search("Windows") !== -1) {
      user_platform = SupportedPlatform.WIN;
      file_manager = "Explorer";
      default_mua = "Outlook";
      data_export_example_path = "c:\\Users\\(username)\\Desktop\\New-Export-Folder";
    } else {
      user_platform = "other";
      file_manager = "File Manager";
      default_mua = "Unsupported";
      data_export_example_path = "/Users/user/Desktop/New-Export-Folder";
    }

    return { user_platform, default_mua, file_manager, data_export_example_path };
  }

  // Public static methods
  // ----------------------------------------------------------------------
  // Description: get the react build mode - not in config info - dependent on what npm script runs (start vs build)
  // NOTE: process.env.NODE_ENV is not configurable. It comes from the react build type (development vs production)
  static isDevelopmentRunMode(): boolean {
    return NODE_ENV === RunMode.development;
  }

  // Description: Turn the loggerMiddleware on for console logging all actions "ON" || "OFF" - stored in .env only
  static turnLoggerOn(): boolean {
    return !!process.env.REACT_APP_LOGGER_ON && process.env.REACT_APP_LOGGER_ON === "ON";
  }

  static buildForApp(): boolean {
    return process.env.REACT_APP_BUILD_MODE === BuildMode.app;
  }

  static buildForWeb(): boolean {
    return process.env.REACT_APP_BUILD_MODE !== BuildMode.app;
  }

  // Description: Determine whether the current account is an express account logged in web
  static buildForExpress(): boolean {
    const account_type = LocalAccountStorage.getStorageItem(AuthenticationConstants.ACCOUNT_TYPE_SESSION_KEY);
    return this.buildForWeb() && account_type === account_types.express;
  }
}

// Description: Returns the APP or WEB port running the app (protocol, host, domain, port piece of the url)
export const APP_BASE_URL = `${AppConfiguration.buildForApp() ? process.env.REACT_APP_PORT : process.env.REACT_APP_WEB_PORT}`;
